import { Formik, Form } from "formik";
import { useState } from "react";
import Modal from "../Modal";
import Toast from "../../Toast/Toast";
import DynamicField from "../../DynamicForm/DynamicField";
import { useLanguage } from "../../../context/LanguageContext";
import { AuthService } from "../../../service/auth.service";
import styles from "./ForgotPasswordModal.module.css";
import {
  getForgotPasswordStep1Schema,
  getForgotPasswordStep2Schema
} from "../../../utils/validationSchema";

type AuthMode = "login" | "register" | "forgot";

interface ToastState {
  message: string;
  type: "success" | "error" | "info";
}

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchMode: (mode: AuthMode) => void;
  resetEnabled?: boolean;
}

export default function ForgotPasswordModal({
  isOpen,
  onClose,
  onSwitchMode,
  resetEnabled = true,
}: ForgotPasswordModalProps) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [savedLogin, setSavedLogin] = useState<string>("");
  const { language, t } = useLanguage();

  const showToast = (message: string, type: "success" | "error" | "info" = "success") => {
    setToast({ message, type });
  };

  const getErrorMessage = (error: any, fallbackKey: string): string => {
    const errorKey = error.response?.data?.error;

    if (errorKey) {
      const translated = t(errorKey);
      if (translated !== errorKey) {
        return translated;
      }
    }

    const apiMessage = error.response?.data?.message;
    if (apiMessage) {
      const translated = t(apiMessage);
      if (translated !== apiMessage) {
        return translated;
      }
    }

    const errorMessage = error.message;
    if (errorMessage) {
      const translated = t(errorMessage);
      if (translated !== errorMessage) {
        return translated;
      }
    }

    return t(fallbackKey);
  };

  const handleClose = () => {
    setStep(1);
    setSavedLogin("");
    onClose();
  };

  if (!resetEnabled) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t("passwordResetDisabled")}
        closeOnOverlayClick={false}
      >
        <div className={styles.disabledMessage}>
          <div className={styles.disabledIcon}>
            <i className="fa fa-lock"></i>
          </div>
          <p className={styles.disabledText}>
            {t("passwordResetCurrentlyDisabled")}
          </p>
          <button
            className={styles.submitButton}
            onClick={() => onSwitchMode("login")}
          >
            {t("backToLogin")}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={step === 1 ? t("forgotTitle") : t("resetPassword")}
        closeOnOverlayClick={false}
      >
        <Formik
          initialValues={{
            login: step === 1 ? "" : savedLogin,
            code: ""
          }}
          validationSchema={
            step === 1
              ? getForgotPasswordStep1Schema(t)
              : getForgotPasswordStep2Schema(t)
          }
          enableReinitialize
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              if (step === 1) {
                const loginValue = values.login.toLowerCase().trim();

                await AuthService.requestPasswordReset({
                  login: loginValue
                });

                setSavedLogin(loginValue);
                showToast(t("resetCodeSentToTelegram"), "success");

                setTimeout(() => {
                  setStep(2);
                }, 1500);

              } else {
                await AuthService.resetPassword({
                  login: savedLogin,
                  code: values.code.trim(),
                });

                showToast(t("passwordResetSuccessCheckTelegram"), "success");
                resetForm();

                setTimeout(() => {
                  setStep(1);
                  setSavedLogin("");
                  onSwitchMode("login");
                }, 2000);
              }
            } catch (error: any) {
              console.error("Error response:", error.response?.data);
              const fallbackKey = step === 1 ? "resetRequestFailed" : "passwordResetFailed";
              showToast(getErrorMessage(error, fallbackKey), "error");
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className={styles.form}>
              {step === 1 ? (
                <>
                  <DynamicField
                    field={{
                      type: "text",
                      name: "login",
                      label: "loginOrEmail",
                      placeholder: "enterLoginOrEmail",
                    }}
                  />

                  <div className={styles.infoBox}>
                    <p className={styles.infoText}>
                      <i className="fa fa-info-circle"></i> {t("resetCodeWillBeSentToTelegram")}
                    </p>
                  </div>

                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t("loading") : t("sendResetCode")}
                  </button>

                  <div className={styles.links}>
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => onSwitchMode("login")}
                    >
                      {t("backToLogin")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <DynamicField
                    field={{
                      type: "text",
                      name: "login",
                      label: "loginOrEmail",
                      placeholder: "enterLoginOrEmail",
                      disabled: true,
                    }}
                  />

                  <DynamicField
                    field={{
                      type: "text",
                      name: "code",
                      label: "resetCode",
                      placeholder: "enterResetCodeFromTelegram",
                    }}
                  />

                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t("loading") : t("resetPassword")}
                  </button>

                  <div className={styles.links}>
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => {
                        setStep(1);
                        setSavedLogin("");
                      }}
                    >
                      {t("back")}
                    </button>
                  </div>
                </>
              )}
            </Form>
          )}
        </Formik>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}